import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SearchList, { SearchListColumn, SearchListConfig } from '../components/SearchList';
import { FilterAction } from '../components/FilterBar';
import { useLayout } from '../contexts/LayoutContext';
import { PATH_SEGMENTS } from '../config/paths';
import { utteranceDetailFilters } from '../config/filterConfigs';
import { api, fetchData } from '../services/api';
import { usePagination } from '../hooks/usePagination';
import {
  TrainingExecutionMetadata,
  UtteranceAnnotation,
  UtteranceEntityAnnotation,
  UtteranceExecutionRecord,
  UtteranceRecord,
  UtteranceSourceDetailData,
  UtteranceSourceDetailResponse
} from '../types/api';
import '../styles/screens/UtteranceDetail.css';

interface AnnotationSourceDescriptor {
  key: string;
  label: string;
  type: 'baseline' | 'execution';
  execution?: UtteranceExecutionRecord;
}

type AnnotationTypeKey = 'intents' | 'entities' | 'mmts';

const detailFilterConfig = utteranceDetailFilters;

const slugify = (value: string) =>
  value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

const formatExecutionLabel = (
  execution: UtteranceExecutionRecord,
  index: number
): string => {
  const parts: string[] = [];
  if (execution.source) {
    parts.push(execution.source);
  } else if (execution.name) {
    parts.push(execution.name);
  } else {
    parts.push(`Execution ${index + 1}`);
  }
  if (execution.version) {
    parts.push(`v${execution.version}`);
  }
  return parts.join(' ');
};

const getAnnotationList = (
  row: UtteranceRecord,
  annotationName: string,
  type: AnnotationTypeKey
): (UtteranceAnnotation | UtteranceEntityAnnotation)[] => {
  const list = row[type] || [];
  return list.filter(item => item.name === annotationName);
};

const getExecutionAnnotationList = (
  rowId: number,
  execution: UtteranceExecutionRecord,
  annotationName: string,
  type: AnnotationTypeKey
): (UtteranceAnnotation | UtteranceEntityAnnotation)[] => {
  let map:
    | Record<string, UtteranceAnnotation[]>
    | Record<string, UtteranceEntityAnnotation[]>
    | undefined;

  if (type === 'intents') {
    map = execution.intent_annotations;
  } else if (type === 'entities') {
    map = execution.entity_annotations;
  } else {
    map = execution.mmt_annotations;
  }

  if (!map) return [];
  const entry = map[rowId] || map[String(rowId)];
  if (!entry) return [];
  return entry.filter(item => item.name === annotationName);
};

type AnnotationMatrixRow = {
  name: string;
  sources: Record<
    string,
    {
      present: boolean;
      value?: string;
    }
  >;
};

const buildAnnotationMatrixRows = (
  row: UtteranceRecord,
  sources: AnnotationSourceDescriptor[],
  type: AnnotationTypeKey
): AnnotationMatrixRow[] => {
  const rows = new Map<string, AnnotationMatrixRow>();

  const addEntry = (
    annotationName: string,
    source: AnnotationSourceDescriptor,
    annotations: (UtteranceAnnotation | UtteranceEntityAnnotation)[]
  ) => {
    const key = annotationName;
    if (!rows.has(key)) {
      rows.set(key, { name: annotationName, sources: {} });
    }
    const value =
      type === 'entities'
        ? (annotations as UtteranceEntityAnnotation[])
            .map(item => item.value)
            .filter(Boolean)
            .join(', ')
        : undefined;
    rows.get(key)!.sources[source.key] = {
      present: annotations.length > 0,
      value
    };
  };

  // Build union across all sources
  sources.forEach(source => {
    if (source.type === 'baseline') {
      (row[type] || []).forEach(annotation => addEntry(annotation.name, source, [annotation]));
    } else {
      const map =
        type === 'intents'
          ? source.execution?.intent_annotations
          : source.execution?.entity_annotations;
      const list = map ? map[row.id] || map[String(row.id)] || [] : [];
      list.forEach(annotation => {
        addEntry(annotation.name, source, [annotation]);
      });
    }
  });

  return Array.from(rows.values()).sort((a, b) => a.name.localeCompare(b.name));
};

const renderAnnotationMatrix = (
  row: UtteranceRecord,
  sources: AnnotationSourceDescriptor[],
  type: AnnotationTypeKey
) => {
  if (sources.length === 0) {
    return <div className="annotation-cell-empty">No annotations</div>;
  }

  const rows = buildAnnotationMatrixRows(row, sources, type);
  if (rows.length === 0) {
    return <div className="annotation-cell-empty">No annotations</div>;
  }

  const isEntity = type === 'entities';

  return (
    <table className="annotation-matrix">
      <thead>
        <tr>
          <th className="annotation-matrix-name">Name</th>
          {sources.map(source => (
            <th key={source.key} className="annotation-matrix-source">
              {source.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map(rowItem => (
          <tr key={rowItem.name}>
            <td className="annotation-matrix-name">{rowItem.name}</td>
            {sources.map(source => {
              const entry = rowItem.sources[source.key];
              const present = entry?.present;
              const value = entry?.value;
              const nonBaseline = source.type !== 'baseline' && present;
              return (
                <td
                  key={source.key}
                  className={`annotation-matrix-cell ${nonBaseline ? 'non-baseline-present' : ''}`}
                >
                  {isEntity ? (present ? value || '—' : '—') : present ? '✓' : '✕'}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const buildAnnotationStatusText = (
  row: UtteranceRecord,
  annotationName: string,
  annotationType: AnnotationTypeKey,
  source: AnnotationSourceDescriptor
) => {
  const annotations =
    source.type === 'baseline'
      ? getAnnotationList(row, annotationName, annotationType)
      : getExecutionAnnotationList(
          row.id,
          source.execution as UtteranceExecutionRecord,
          annotationName,
          annotationType
        );

  const present = annotations.length > 0;
  if (!present) {
    return `${source.label}: ✕`;
  }

  if (annotationType === 'entities') {
    const values = (annotations as UtteranceEntityAnnotation[])
      .map(item => item.value)
      .filter(Boolean)
      .join(', ');
    return values
      ? `${source.label}: ✓ (${values})`
      : `${source.label}: ✓`;
  }

  return `${source.label}: ✓`;
};

const AnnotationCell: React.FC<{
  row: UtteranceRecord;
  annotationName: string;
  annotationType: AnnotationTypeKey;
  sources: AnnotationSourceDescriptor[];
}> = ({ row, annotationName, annotationType, sources }) => {
  if (sources.length === 0) {
    return <div className="annotation-cell-empty">No annotations</div>;
  }

  return (
    <div className="annotation-cell">
      {sources.map(source => {
        const annotations =
          source.type === 'baseline'
            ? getAnnotationList(row, annotationName, annotationType)
            : getExecutionAnnotationList(
                row.id,
                source.execution as UtteranceExecutionRecord,
                annotationName,
                annotationType
              );

        const present = annotations.length > 0;
        const entityValues =
          annotationType === 'entities' && present
            ? (annotations as UtteranceEntityAnnotation[])
                .map(item => item.value)
                .filter(Boolean)
                .join(', ')
            : null;

        return (
          <div className="annotation-status-row" key={`${source.key}-${annotationName}`}>
            <span className="annotation-status-label">{source.label}</span>
            <div className="annotation-status-indicator-wrapper">
              <span
                className={`annotation-status-indicator ${
                  present ? 'present' : 'absent'
                }`}
              >
                {present ? '✓' : '✕'}
              </span>
              {entityValues && (
                <span className="annotation-entity-value">{entityValues}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const UtteranceSourceDetail: React.FC = () => {
  const { utsid } = useParams<{ utsid: string }>();
  const navigate = useNavigate();
  const { updateHeaderProps } = useLayout();
  const [detail, setDetail] = useState<UtteranceSourceDetailData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [searchQuery, setSearchQuery] = useState<string>('');

  const {
    paginationState,
    updatePagination,
    handlePageChange,
    handleSort
  } = usePagination(
    { disableAutoUrlUpdate: true },
    { field: 'id', direction: 'asc' }
  );
  const updatePaginationRef = useRef(updatePagination);
  useEffect(() => {
    updatePaginationRef.current = updatePagination;
  }, [updatePagination]);

  const fetchUtteranceDetail = useCallback(async () => {
    if (!utsid) {
      setError('Utterance source id missing');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.set('page', String(paginationState.currentPage));
      params.set('per_page', String(paginationState.itemsPerPage));
      params.set('order_by', paginationState.sortConfig.field);
      params.set('order_direction', paginationState.sortConfig.direction);
      Object.entries(filters).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        params.set(key, String(value));
      });

      const response = await fetchData<UtteranceSourceDetailResponse>(
        `${api(PATH_SEGMENTS.UTTERANCE, utsid)}?${params.toString()}`
      );

      const payload = response.data.uttreance_sources;
      setDetail(payload);

      const pagination = response.data.pagination;
      if (pagination) {
        const nextCurrentPage = pagination.current_page || 1;
        const nextTotalPages = pagination.total_pages || paginationState.totalPages;
        const nextItemsPerPage = pagination.items_per_page || paginationState.itemsPerPage;

        if (
          nextCurrentPage !== paginationState.currentPage ||
          nextTotalPages !== paginationState.totalPages ||
          nextItemsPerPage !== paginationState.itemsPerPage
        ) {
          updatePaginationRef.current({
            currentPage: nextCurrentPage,
            totalPages: nextTotalPages,
            itemsPerPage: nextItemsPerPage
          });
        }
      }
    } catch (err: any) {
      console.error('Failed to load utterance source', err);
      setError('Failed to load utterance source');
    } finally {
      setLoading(false);
    }
  }, [utsid, paginationState]);

  useEffect(() => {
    fetchUtteranceDetail();
  }, [fetchUtteranceDetail]);

  useEffect(() => {
    updateHeaderProps({
      pageTitle: detail?.utterance_source?.name
        ? `Utterances • ${detail.utterance_source.name}`
        : 'Utterances',
      showInteractionExId: false
    });
  }, [detail, updateHeaderProps]);

  const hasBaselineAnnotations = useMemo(() => {
    if (!detail) return false;
    return detail.utterances.some(
      item =>
        (item.intents && item.intents.length > 0) ||
        (item.entities && item.entities.length > 0) ||
        (item.mmts && item.mmts.length > 0)
    );
  }, [detail]);

  const annotationSources: AnnotationSourceDescriptor[] = useMemo(() => {
    if (!detail) return [];
    const sources: AnnotationSourceDescriptor[] = [];
    if (hasBaselineAnnotations) {
      const baselineLabel = detail.summary?.baseline_version
        ? `Baseline v${detail.summary.baseline_version}`
        : 'Baseline';
      sources.push({
        key: 'baseline',
        label: baselineLabel,
        type: 'baseline'
      });
    }
    detail.executions?.forEach((execution, index) => {
      sources.push({
        key: `execution-${execution.annotation_type_id ?? index}`,
        label: formatExecutionLabel(execution, index),
        type: 'execution',
        execution
      });
    });
    return sources;
  }, [detail, hasBaselineAnnotations]);

  const annotationNames = useMemo(() => {
    const intentSet = new Set<string>();
    const entitySet = new Set<string>();
    const momentSet = new Set<string>();

    if (detail) {
      detail.utterances.forEach(item => {
        item.intents?.forEach(intent => intentSet.add(intent.name));
        item.entities?.forEach(entity => entitySet.add(entity.name));
        item.mmts?.forEach(mmt => momentSet.add(mmt.name));
      });

      detail.executions?.forEach(execution => {
        Object.values(execution.intent_annotations || {}).forEach(list =>
          list?.forEach(intent => intentSet.add(intent.name))
        );
        Object.values(execution.entity_annotations || {}).forEach(list =>
          list?.forEach(entity => entitySet.add(entity.name))
        );
        Object.values(execution.mmt_annotations || {}).forEach(list =>
          list?.forEach(mmt => momentSet.add(mmt.name))
        );
      });
    }

    return {
      intents: Array.from(intentSet).sort(),
      entities: Array.from(entitySet).sort(),
      mmts: Array.from(momentSet).sort()
    };
  }, [detail]);

  const columns: SearchListColumn[] = useMemo(
    () => [
      {
        key: 'utterance-text',
        header: 'Utterance',
        width: '1.8fr',
        render: (item: UtteranceRecord) => (
          <div>
            <div>{item.text}</div>
            {item.create_time && (
              <div className="utterance-source-meta">
                Added {new Date(item.create_time).toLocaleString()}
              </div>
            )}
          </div>
        )
      },
      {
        key: 'utterance-intents',
        header: 'Intents (baseline vs executions)',
        width: '1.6fr',
        allowOverflow: true,
        render: (item: UtteranceRecord) =>
          renderAnnotationMatrix(item, annotationSources, 'intents')
      },
      {
        key: 'utterance-entities',
        header: 'Entities (baseline vs executions)',
        width: '1.6fr',
        allowOverflow: true,
        render: (item: UtteranceRecord) =>
          renderAnnotationMatrix(item, annotationSources, 'entities')
      }
    ],
    [annotationSources]
  );

  const exportColumns = useMemo(() => {
    const cols: Array<{ header: string; value: (item: UtteranceRecord) => string }> = [
      {
        header: 'Utterance',
        value: (item: UtteranceRecord) => item.text
      }
    ];

    const buildExportColumns = (type: AnnotationTypeKey, names: string[], labelPrefix: string) => {
      names.forEach(name => {
        cols.push({
          header: `${labelPrefix}: ${name}`,
          value: (item: UtteranceRecord) =>
            annotationSources
              .map(source => buildAnnotationStatusText(item, name, type, source))
              .join(' | ')
        });
      });
    };

    buildExportColumns('intents', annotationNames.intents, 'Intent');
    buildExportColumns('entities', annotationNames.entities, 'Entity');
    buildExportColumns('mmts', annotationNames.mmts, 'Moment');
    return cols;
  }, [annotationNames, annotationSources]);

  const searchListConfig: SearchListConfig = {
    columns,
    filterConfig: detailFilterConfig,
    showSearch: true,
    showFilterButton: true,
    showFilterBar: true,
    showCreateButton: false,
    showBuildUpload: false,
    itemsPerPage: paginationState.itemsPerPage,
    defaultSort: paginationState.sortConfig,
    exportConfig: {
      enabled: true,
      filenameBase: 'utterance-source',
      columns: exportColumns
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters(prev => ({
      ...prev,
      ut_search: searchQuery.trim() || undefined
    }));
  };

  const handleFilterActionChange = (action: FilterAction) => {
    if (action.type === 'REMOVE') {
      const newFilters = { ...filters };
      delete newFilters[action.key];
      setFilters(newFilters);
    }
  };

  const summaryCards = detail
    ? [
        {
          label: 'Total Utterances',
          value: detail.summary?.total_utterances ?? 0
        },
        {
          label: 'Baseline Version',
          value:
            detail.summary?.baseline_version !== undefined &&
            detail.summary?.baseline_version !== null
              ? detail.summary.baseline_version
              : '—'
        },
        {
          label: 'Execution Runs',
          value: detail.summary?.execution_runs ?? 0
        }
      ]
    : [];

  const sourceMeta = detail?.utterance_source;

  return (
    <div className="utterance-detail-page">
      <div className="utterance-detail-header">
        <div>
          <h2>{sourceMeta?.name || 'Utterance Source'}</h2>
          <div className="utterance-source-meta">
            {sourceMeta?.source && <span>Source: {sourceMeta.source}</span>}
            {sourceMeta?.sourcing_time && (
              <span>
                Sourcing time: {new Date(sourceMeta.sourcing_time).toLocaleString()}
              </span>
            )}
          </div>
        </div>
        <div className="utterance-detail-actions">
          <button onClick={() => navigate(-1)}>Back</button>
        </div>
      </div>

      {summaryCards.length > 0 && (
        <div className="utterance-summary-grid">
          {summaryCards.map(card => (
            <div className="utterance-summary-card" key={card.label}>
              <span className="label">{card.label}</span>
              <span className="value">{card.value}</span>
            </div>
          ))}
        </div>
      )}

      <div className="utterance-detail-table-wrapper">
        <SearchList
          config={searchListConfig}
          data={detail?.utterances ?? []}
          loading={loading}
          error={error}
          filters={filters}
          searchQuery={searchQuery}
          currentPage={paginationState.currentPage}
          totalPages={paginationState.totalPages}
          itemsPerPage={paginationState.itemsPerPage}
          totalItems={detail?.summary?.total_utterances}
          sortConfig={paginationState.sortConfig}
          onSearchChange={setSearchQuery}
          onSearchSubmit={handleSearchSubmit}
          onFilterChange={setFilters}
          onFilterActionChange={handleFilterActionChange}
          onPageChange={handlePageChange}
          onSort={handleSort}
          emptyMessage="No utterances found"
          loadingMessage="Loading utterances..."
        />
      </div>
    </div>
  );
};

export default UtteranceSourceDetail;

